import {Injectable} from '@angular/core';
import {from as fromRxjs, Observable} from 'rxjs';
import {AsaxElasticService, buildQueryTermsEqual} from 'asax-elastic';
import {catchError, map, switchMap} from 'rxjs/operators';


interface Limit {
  readonly from: number;
  readonly size: number;
}

export interface SearchTablePage {
  readonly index?: string;
  readonly from?: number;
  readonly size?: number;
  readonly query?: any;
  readonly aggregations?: any;
  readonly sort?: any;
  readonly source?: string[];
  readonly filters?: any[];
  readonly filtersMappingSearchProperties?: any;
}

export interface SearchResultTablePage {
  readonly rows?: any[];
  readonly totalRows?: number;
  readonly from?: number;
}

const maximumWindowElasticResultSize = 10000;

@Injectable({
  providedIn: 'root'
})
export class SearchTableService {
  constructor(private readonly asaxElasticService: AsaxElasticService) {
  }

  search(searchTablePage: SearchTablePage): Observable<SearchResultTablePage> {
    return this.asaxElasticService.searchByQuery({
      index: searchTablePage.index,
      body: {
        _source: searchTablePage.source,
        from: searchTablePage.from,
        size: searchTablePage.size,
        sort: searchTablePage.sort,
        query: searchTablePage.query
      }
    }).pipe(
      map((data: any) => ({
        totalRows: data.hits.total,
        rows: data.hits?.hits?.map(({_source}) => ({..._source})) || [],
        from: searchTablePage.from
      })),
      catchError(() => [])
    );
  }

  searchAllRows(searchTablePage: SearchTablePage): Observable<any[]> {
    return this.asaxElasticService.searchByQuery({
      index: searchTablePage.index,
      body: {
        from: 0,
        size: 0,
        sort: searchTablePage.sort,
        query: searchTablePage.query
      }
    }).pipe(
      map((data: any) => data.hits.total),
      switchMap((total: number) => this.searchAllRowsByChunks(searchTablePage, total)),
      catchError(() => [])
    );
  }

  searchAllRowsByChunks(searchTablePage: SearchTablePage, numberOfRows: number): Observable<any[]> {
    const promises: Promise<any>[] = [];

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index: searchTablePage.index,
        body: {
          _source: searchTablePage.source,
          from: limit.from,
          size: limit.size,
          sort: searchTablePage.sort,
          query: searchTablePage.query
        }
      }).toPromise();

      promises.push(promiseElasticSearchChunk);
    }

    return fromRxjs(Promise.all(promises)).pipe(
      map((responses: any) => responses.map(({hits}) => hits.hits.map(({_source}) => ({..._source})))),
      map((rows: any) => rows.flat()),
      catchError(() => [])
    );
  }

  searchAllRowsByIds(index: string, ids: string[], source: string[]): Observable<any[]> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('_id', idsChunk);

      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index, body: {_source: source, size: limit.size, query: idsChunkDSLQuery}
      }).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return fromRxjs(Promise.all(promises)).pipe(
      map((responses: any) => responses.map(({hits}) => hits.hits.map(({_source}) => ({..._source})))),
      map((rows: any) => rows.flat()),
      catchError(() => [])
    );
  }

  searchAggregations(searchTablePage: SearchTablePage): Observable<any> {
    return this.asaxElasticService.searchByQuery({
      index: searchTablePage.index,
      body: {
        from: 0,
        size: 0,
        query: searchTablePage.query,
        aggregations: searchTablePage.aggregations
      }
    }).pipe(
      map(({aggregations}) => aggregations),
      catchError(() => undefined)
    );
  }
}

const getLimits = (total: number, size: number): Limit[] => {
  const array = Array.from(new Array(Math.floor(total / size)), (val, index) => index + 1);
  const limits = array.map((value, index) => ({from: index * size, size}));
  limits.push({from: limits.length * size, size: total % size});
  return limits;
};
